import React, { useState } from 'react';
import FestivalAmbient from '../../components/festival/FestivalAmbient';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { LayoutDashboard, FolderOpen, LogOut, Menu, X, User, ShoppingCart, MessageSquare, FileText /* , Sparkles */ } from 'lucide-react';

const navItems = [
  { to: '/client', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/client/projects', label: 'My Projects', icon: FolderOpen },
  { to: '/client/purchase-orders', label: 'Purchase Orders', icon: ShoppingCart },
  { to: '/client/sales', label: 'Sales Invoices', icon: FileText },
  { to: '/client/support', label: 'Support', icon: MessageSquare },
  // { to: '/client/ask-moji', label: 'Ask Moji', icon: Sparkles },   // Ask Moji — hidden for now
];

// Sidebar is defined OUTSIDE the layout component so it is never remounted
// on re-renders, which would reset the nav scroll position to the top.
function ClientSidebar({ mobile, onCloseMobile, user, settings, onLogout }) {
  return (
    <div className={`flex flex-col h-full bg-gradient-to-b from-slate-900 to-slate-800 ${mobile ? 'w-72' : 'w-64'}`}>
      {/* Logo */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-700">
        <img src="/Logo.png" alt="Logo" className="w-9 h-9 rounded-xl object-contain flex-shrink-0 bg-white p-0.5" />
        <div>
          <p className="text-white font-bold text-sm leading-tight">
            {settings.company_name?.split(" ").slice(0, -1).join(" ") || "Workshop"}
          </p>
          <p className="text-amber-400 text-xs">
            {settings.company_name?.split(" ").slice(-1)[0] || "Manager"}
          </p>
        </div>
      </div>

      {/* Client Badge */}
      <div className="mx-3 mt-3 px-3 py-2 bg-blue-500/20 border border-blue-500/30 rounded-xl">
        <p className="text-blue-300 text-xs font-semibold text-center">🔷 Client Portal</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto mt-2">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onCloseMobile}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                isActive
                  ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
              }`
            }
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      <div className="p-3 border-t border-slate-700">
        <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-700/50">
          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">
            {user?.name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-semibold truncate">{user?.name}</p>
            <p className="text-slate-400 text-xs">Client</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="mt-2 flex items-center gap-3 px-3 py-2 rounded-xl text-slate-400 hover:text-red-400 hover:bg-slate-700 transition-all w-full"
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">Logout</span>
        </button>
      </div>
    </div>
  );
}

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex flex-shrink-0">
        <ClientSidebar
          mobile={false}
          onCloseMobile={() => setMobileOpen(false)}
          user={user}
          settings={settings}
          onLogout={handleLogout}
        />
      </div>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">
            <ClientSidebar
              mobile
              onCloseMobile={() => setMobileOpen(false)}
              user={user}
              settings={settings}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 md:hidden"
          >
            {mobileOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-blue-500" />
            <span className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium border border-blue-200">
              {user?.name}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
          <FestivalAmbient />
        </main>
      </div>
    </div>
  );
}
